import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Champ,
  Confirmer,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useAutoFocusOnMount,
} from "@dozka/ui";

/**
 * Dialog imperatif — remplace `window.alert/confirm/prompt` natifs (UI browser
 * degueu, hors theme, hors i18n). Monte un seul provider au root, puis chaque
 * call site appelle `await dialog.confirm(...)` etc. — meme shape que l'API
 * native (return Promise<boolean | string | null>).
 *
 * Depuis le 01-11-2026 les trois se dessinent avec la librairie : `confirm`
 * est `<Confirmer>` (le focus sur « Annuler » quand la question est
 * `danger`), `alert` et `prompt` sont des `<Modal>` (`sm`, `alertdialog`) au
 * même rang — passer au-dessus de tout (z 500), comme `<Confirmer>`.
 *
 * Pattern : un seul dialog visible a la fois. Si plusieurs appels arrivent en
 * meme temps (rare), le 2eme attend qu'on close le 1er — c'est le meme
 * comportement que `window.confirm` natif (bloquant).
 */

interface ConfirmOptions {
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
}

interface AlertOptions {
  title: string;
  message: string | ReactNode;
  okLabel?: string;
  variant?: "info" | "error";
}

interface PromptOptions {
  title: string;
  message?: string | ReactNode;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface DialogApi {
  alert: (opts: AlertOptions) => Promise<void>;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type Pending =
  | {
      kind: "confirm";
      opts: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "alert";
      opts: AlertOptions;
      resolve: () => void;
    }
  | {
      kind: "prompt";
      opts: PromptOptions;
      resolve: (v: string | null) => void;
    };

const DialogContext = createContext<DialogApi | null>(null);

export function useDialog(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used inside <DialogProvider>");
  }
  return ctx;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);

  const api = useMemo<DialogApi>(
    () => ({
      alert: (opts) =>
        new Promise<void>((resolve) => {
          setPending({ kind: "alert", opts, resolve });
        }),
      confirm: (opts) =>
        new Promise<boolean>((resolve) => {
          setPending({ kind: "confirm", opts, resolve });
        }),
      prompt: (opts) =>
        new Promise<string | null>((resolve) => {
          setPending({ kind: "prompt", opts, resolve });
        }),
    }),
    [],
  );

  function close() {
    setPending(null);
  }

  function handleConfirm() {
    if (!pending) return;
    if (pending.kind === "confirm") pending.resolve(true);
    if (pending.kind === "alert") pending.resolve();
    close();
  }

  function handleCancel() {
    if (!pending) return;
    if (pending.kind === "confirm") pending.resolve(false);
    if (pending.kind === "alert") pending.resolve();
    if (pending.kind === "prompt") pending.resolve(null);
    close();
  }

  function handlePromptSubmit(value: string) {
    if (pending?.kind !== "prompt") return;
    pending.resolve(value);
    close();
  }

  // Les trois se portent sur `document.body` (la Modale de la librairie le
  // fait) : un ancêtre qui crée un stacking context (transform, filter,
  // isolation, opacity<1) ne les laisse donc pas peindre SOUS une modale d'un
  // autre contexte racine. z 500 : au-dessus de la BottomBar (300), de
  // l'installation d'app et du journal des nouveautés (400).
  return (
    <DialogContext.Provider value={api}>
      {children}
      {pending?.kind === "confirm" && (
        <Confirmer
          ouvert
          titre={pending.opts.title}
          texte={pending.opts.message}
          confirmer={pending.opts.confirmLabel ?? t("common.confirm")}
          annuler={pending.opts.cancelLabel ?? t("common.cancel")}
          danger={pending.opts.variant === "danger"}
          closeLabel={t("common.close")}
          onConfirmer={handleConfirm}
          onAnnuler={handleCancel}
        />
      )}
      {pending?.kind === "alert" && (
        <AlertModal opts={pending.opts} onConfirm={handleConfirm} />
      )}
      {pending?.kind === "prompt" && (
        <PromptModal
          opts={pending.opts}
          onSubmit={handlePromptSubmit}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
}

function AlertModal({
  opts,
  onConfirm,
}: {
  opts: AlertOptions;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const okLabel = opts.okLabel ?? t("common.confirm");

  // Entrée valide, comme Échap : une alerte n'a qu'une réponse.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Enter") {
        e.preventDefault();
        onConfirm();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onConfirm]);

  return (
    <Modal
      isOpen
      onClose={onConfirm}
      size="sm"
      role="alertdialog"
      ariaLabel={opts.title}
      zIndex={500}
      closeLabel={t("common.close")}
    >
      <ModalHeader title={opts.title} onClose={onConfirm} closeLabel={t("common.close")} />
      <ModalBody
        className={
          "ov-texte " + (opts.variant === "error" ? "text-erreur" : "text-fg")
        }
      >
        {typeof opts.message === "string" ? <p>{opts.message}</p> : opts.message}
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" size="lg" onClick={onConfirm} autoFocus>
          {okLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function PromptModal({
  opts,
  onSubmit,
  onCancel,
}: {
  opts: PromptOptions;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const confirmLabel = opts.confirmLabel ?? t("common.confirm");
  const cancelLabel = opts.cancelLabel ?? t("common.cancel");
  const [value, setValue] = useState(opts.initialValue ?? "");
  const inputRef = useAutoFocusOnMount<HTMLInputElement>();

  // Select-all au mount pour qu'on puisse remplacer la valeur initiale d'un
  // coup (cas rename).
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(id);
  }, [inputRef]);

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      role="alertdialog"
      ariaLabel={opts.title}
      zIndex={500}
      closeLabel={t("common.close")}
    >
      <ModalHeader title={opts.title} onClose={onCancel} closeLabel={t("common.close")} />
      <ModalBody className="flex flex-col gap-3">
        {opts.message && (
          <div className="ov-texte text-muted">
            {typeof opts.message === "string" ? <p>{opts.message}</p> : opts.message}
          </div>
        )}
        <Champ
          ref={inputRef as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
          type="text"
          taille="lg"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit(value);
            }
          }}
          placeholder={opts.placeholder}
          aria-label={opts.title}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="lg" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant="primary" size="lg" onClick={() => onSubmit(value)}>
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
